/**
 * Punkt 42 (Migration 533, Mockup freigegeben 26.09.2026): Kundentyp Privat · Firma · Verein.
 *
 * Der Typ lebt am KUNDEN (`customers.kundentyp`). `kundentypAutomatisch` = aus dem Firmenfeld
 * erkannt („automatisch erkannt – bitte prüfen“); sobald jemand wählt oder bestätigt, ist er fest.
 * Der Vertrag richtet sich danach: Privat ⇒ Privat-Vorlage (falls hinterlegt), Firma und Verein ⇒
 * die bisherige Vorlage.
 */
import { Icon } from "./components";

export type Kundentyp = "privat" | "firma" | "verein";
export const KUNDENTYPEN: readonly Kundentyp[] = ["privat", "firma", "verein"];
export const KUNDENTYP_TEXT: Record<Kundentyp, string> = { privat: "Privat", firma: "Firma", verein: "Verein" };
export const KUNDENTYP_ICON: Record<Kundentyp, string> = { privat: "person", firma: "business", verein: "groups" };

/** Die alte Rateregel — Spiegel von `public.kundentyp_aus_firma` (sql/533). Nur noch Rückfall für
 *  Antworten ohne `kundentyp` (z. B. ältere API-Stände). */
export function kundentypAusFirma(company: string | null | undefined): Kundentyp {
  if (!company || !company.trim()) return "privat";
  return /\be\.\s?v\b|verein/i.test(company) ? "verein" : "firma";
}

export function kundentypVon(c: { kundentyp?: Kundentyp | null; company?: string | null }): Kundentyp {
  return c.kundentyp ?? kundentypAusFirma(c.company);
}

/** Erklärt, warum die Erkennung so ausfiel — derselbe Satz wie im Mockup. */
export function erkennungsGrund(typ: Kundentyp): string {
  if (typ === "privat") return "Erkannt aus dem Firmenfeld: es ist leer, deshalb steht hier „Privat“. Stimmt das nicht, einfach umstellen.";
  if (typ === "verein") return "Erkannt aus dem Firmenfeld: der Name enthält „e.V.“ oder „Verein“, deshalb steht hier „Verein“. Stimmt das nicht, einfach umstellen.";
  return "Erkannt aus dem Firmenfeld: der Name enthält weder „e.V.“ noch „Verein“, deshalb steht hier „Firma“. Stimmt das nicht, einfach umstellen.";
}

export function AutomatischErkanntChip({ text = "automatisch erkannt – bitte prüfen" }: { text?: string }) {
  return (
    <span
      data-testid="kundentyp-automatisch"
      className="inline-flex items-center gap-1 rounded-full bg-[#F0EAE0] px-2 py-0.5 text-[11px] font-semibold text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]"
    >
      <Icon name="auto_awesome" className="text-[13px]" /> {text}
    </span>
  );
}

/** Drei Segmente Privat · Firma · Verein. `wert` hervorgehoben; ein Klick wählt (auch derselbe
 *  Wert — das ist die Bestätigung). */
export function KundentypAuswahl({
  wert,
  onWahl,
  disabled,
  testId = "kundentyp-auswahl",
}: {
  wert: Kundentyp | null;
  onWahl: (typ: Kundentyp) => void;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <div role="radiogroup" aria-label="Kundentyp" data-testid={testId} className="grid grid-cols-3 gap-1.5">
      {KUNDENTYPEN.map((t) => {
        const aktiv = wert === t;
        return (
          <button
            key={t}
            type="button"
            role="radio"
            aria-checked={aktiv}
            disabled={disabled}
            data-testid={`kundentyp-${t}`}
            onClick={() => onWahl(t)}
            className={`flex items-center justify-center gap-1.5 rounded-[9px] border px-2 py-2 text-[12.5px] font-semibold motion-safe:transition-colors disabled:opacity-50 ${
              aktiv
                ? "border-primary bg-primary/10 text-primary dark:bg-primary/15"
                : "border-outline-variant bg-surface-container-lowest text-on-surface-variant hover:border-primary/40 dark:bg-inverse-surface dark:text-inverse-on-surface"
            }`}
          >
            <Icon name={KUNDENTYP_ICON[t]} className="text-[16px]" /> {KUNDENTYP_TEXT[t]}
          </button>
        );
      })}
    </div>
  );
}
